import { useQuery } from '@tanstack/react-query'
import { controlCenterApi } from '@/lib/api'
import StatusBadge from '@/components/StatusBadge'
import PageHeader from '@/components/PageHeader'

type Port = {
  containerPort?: string | number
  hostPort?: string | number
  private?: number
  public?: number
}

type Container = {
  id?: string
  name?: string
  image?: string
  status?: string
  state?: string
  ports?: Port[]
  uptime?: string
  cpu?: number
  memory?: number
}

type InfrastructureResponse = { success?: boolean; containers?: Container[] }

function formatPort(p: Port): string {
  const host = p.hostPort ?? p.public
  const container = p.containerPort ?? p.private
  if (host && container) return `${host}:${container}`
  if (container) return String(container)
  return ''
}

export default function Containers() {
  const { data, isLoading, isError } = useQuery<InfrastructureResponse>({
    queryKey: ['platform', 'infrastructure'],
    queryFn: controlCenterApi.getInfrastructure,
  })

  const containers = data?.containers ?? []
  const running = containers.filter((c) => String(c.status || c.state || '').toLowerCase() === 'running').length

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Containers"
        description={`${running} of ${containers.length} containers running.`}
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load containers. Confirm Docker socket is reachable and aios-backend is running.
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Image</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Ports</th>
              <th className="px-4 py-2.5 font-medium">Uptime</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : containers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  No containers visible. Is the Docker socket mounted into aios-backend?
                </td>
              </tr>
            ) : (
              containers.map((c) => (
                <tr key={c.id || c.name} className="border-t border-border">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{c.name || c.id}</div>
                    {c.id ? (
                      <div className="text-xs text-muted-foreground font-mono">{c.id.slice(0, 12)}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{c.image || '—'}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={c.status || c.state} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    {Array.isArray(c.ports) && c.ports.length > 0
                      ? c.ports.map(formatPort).filter(Boolean).join(', ')
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.uptime || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
